import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AdminService } from './services/admin.service';
import { BottomNavComponent } from './components/bottom-nav.component';
import { LocationFilterComponent } from './components/location-filter.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, BottomNavComponent, LocationFilterComponent],
  template: `
    <header class="topbar">
      <div class="container nav">
        <a routerLink="/" class="brand" aria-label="ShowSpot Ireland home">
          <span class="brand-mark" aria-hidden="true">
            <span class="pin-logo">
              <span class="play-triangle"></span>
            </span>
          </span>
          <span class="brand-copy">
            <strong><span class="brand-show">Show</span><span class="brand-spot">Spot</span></strong>
            <small>Ireland</small>
          </span>
        </a>
        <nav id="primary-nav" aria-label="Primary navigation">
          <a routerLink="/" routerLinkActive="active" [routerLinkActiveOptions]="{ exact: true }">Search / Home</a>
          <a routerLink="/movies" routerLinkActive="active">Movies</a>
          <a routerLink="/cinemas" routerLinkActive="active">Cinemas</a>
          <a routerLink="/admin/scrape" routerLinkActive="active">Admin</a>
        </nav>
        <div class="nav-actions">
          <app-location-filter />
          <button class="icon-btn scrape-btn" type="button" (click)="runScrape()" [disabled]="scraping" [attr.aria-busy]="scraping" title="Refresh live showtimes">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20 12a8 8 0 1 1-2.34-5.66" />
              <path d="M20 4v5h-5" />
            </svg>
            <span>{{ scraping ? 'Refreshing' : 'Refresh' }}</span>
          </button>
        </div>
      </div>
      <div class="scrape-status" *ngIf="scrapeMessage" [class.error]="scrapeError">{{ scrapeMessage }}</div>
    </header>
    <main>
      <router-outlet />
    </main>
    <app-bottom-nav />
  `,
  styles: [
    `
      .topbar {
        position: sticky;
        top: 0;
        z-index: 30;
        border-bottom: 1px solid var(--topbar-line);
        background: var(--topbar-bg);
        backdrop-filter: blur(20px);
      }

      .nav {
        min-height: 68px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 14px;
      }

      .brand {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
        flex: 0 1 auto;
      }

      .brand-mark {
        width: 40px;
        height: 40px;
        border-radius: 14px;
        flex: 0 0 auto;
        display: grid;
        place-items: center;
        background:
          radial-gradient(circle at 50% 86%, rgba(255, 63, 110, 0.46), transparent 24%),
          radial-gradient(circle at 45% 18%, rgba(88, 224, 255, 0.16), transparent 48%),
          linear-gradient(145deg, #071433, #020818 70%);
        box-shadow:
          inset 0 0 0 1px rgba(122, 207, 255, 0.12),
          0 0 34px rgba(88, 224, 255, 0.22);
        position: relative;
        overflow: hidden;
      }

      .pin-logo {
        width: 24px;
        height: 29px;
        position: relative;
        display: grid;
        place-items: center;
        filter: drop-shadow(0 0 10px rgba(88, 224, 255, 0.38));
      }

      .pin-logo::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50% 50% 54% 54%;
        clip-path: polygon(50% 100%, 18% 58%, 12% 38%, 16% 18%, 30% 5%, 50% 0, 70% 5%, 84% 18%, 88% 38%, 82% 58%);
        background: linear-gradient(180deg, #0cecff 0%, #2e8cff 48%, #a75bff 82%, #ff74dc 100%);
      }

      .pin-logo::after {
        content: '';
        position: absolute;
        width: 13px;
        height: 13px;
        border-radius: 999px;
        background: #071126;
        box-shadow: inset 0 0 0 1px rgba(122, 207, 255, 0.08);
        top: 7px;
      }

      .play-triangle {
        width: 0;
        height: 0;
        border-top: 4px solid transparent;
        border-bottom: 4px solid transparent;
        border-left: 7px solid #34a8ff;
        position: relative;
        z-index: 2;
        margin-left: 2px;
      }

      .brand-copy {
        min-width: 0;
      }

      .brand strong {
        display: block;
        font-size: 21px;
        letter-spacing: 0;
        white-space: nowrap;
        line-height: 1;
      }

      .brand-show {
        color: var(--text);
        text-shadow: 0 0 18px rgba(246, 248, 255, 0.08);
      }

      .brand-spot {
        background: linear-gradient(100deg, #0cecff 0%, #2e8cff 45%, #8c5dff 78%, #ff4fca 100%);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        text-shadow: 0 0 22px rgba(88, 224, 255, 0.18);
      }

      .brand small {
        display: block;
        width: fit-content;
        margin-top: 3px;
        border: 1px solid rgba(88, 224, 255, 0.62);
        border-radius: 7px;
        padding: 1px 6px 2px;
        color: var(--cyan);
        background: rgba(4, 14, 32, 0.62);
        font-size: 11px;
        font-weight: 950;
        letter-spacing: 0.26em;
        text-transform: uppercase;
        box-shadow: 0 0 18px rgba(88, 224, 255, 0.11);
      }

      nav {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        margin-left: auto;
      }

      nav a {
        min-height: 40px;
        border-radius: 999px;
        padding: 10px 14px;
        color: var(--muted);
        font-weight: 850;
        transition: color 150ms ease, background 150ms ease, box-shadow 150ms ease;
      }

      nav a.active,
      nav a:hover {
        color: var(--nav-active);
        background: var(--nav-hover);
        box-shadow: inset 0 0 0 1px var(--line);
      }

      .nav-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .icon-btn {
        min-height: 40px;
        border: 1px solid var(--line);
        border-radius: 999px;
        color: var(--text);
        background: var(--control-bg);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        cursor: pointer;
        font-weight: 850;
        transition: transform 150ms ease, border-color 150ms ease, background 150ms ease;
      }

      .icon-btn:hover:not(:disabled) {
        transform: translateY(-1px);
        border-color: var(--line-strong);
        background: var(--control-hover);
      }

      .icon-btn:disabled {
        cursor: wait;
        opacity: .68;
      }

      .icon-btn svg {
        width: 18px;
        height: 18px;
        fill: none;
        stroke: currentColor;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      .scrape-btn {
        padding: 0 14px;
        color: var(--text);
        border: 0;
        background: linear-gradient(135deg, var(--red), var(--violet) 52%, var(--blue));
        box-shadow: 0 12px 34px rgba(79, 124, 255, 0.22);
      }

      .scrape-status {
        width: min(1180px, calc(100% - 40px));
        margin: -8px auto 10px;
        color: var(--green);
        font-size: 12px;
        font-weight: 850;
      }

      .scrape-status.error { color: var(--danger); }

      main { min-height: calc(100vh - 68px); }

      @media (max-width: 767px) {
        .nav {
          min-height: 64px;
          position: relative;
          padding: 8px 0;
        }

        .brand {
          max-width: calc(100% - 150px);
        }

        .brand-mark {
          width: 38px;
          height: 38px;
          border-radius: 12px;
        }

        .pin-logo {
          width: 23px;
          height: 27px;
        }

        .brand strong {
          font-size: 19px;
        }

        .brand small {
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 9px;
          letter-spacing: 0.18em;
          padding-inline: 5px;
        }

        nav {
          display: none;
        }

        .nav-actions {
          margin-left: auto;
          gap: 6px;
        }

        .scrape-btn {
          width: 42px;
          padding: 0;
        }

        .scrape-btn span {
          display: none;
        }

        .scrape-btn {
          min-height: 42px;
          border-radius: 14px;
        }

        .scrape-status {
          width: min(100% - 18px, 1180px);
          margin: 0 auto 8px;
        }
      }

      @media (max-width: 420px) {
        .brand {
          max-width: calc(100% - 178px);
        }
      }
    `,
  ],
})
export class AppComponent {
  scraping = false;
  scrapeMessage = '';
  scrapeError = false;
  private messageTimer?: ReturnType<typeof setTimeout>;

  constructor(private readonly admin: AdminService) {}

  runScrape() {
    if (this.scraping) return;
    this.scraping = true;
    this.setScrapeMessage('Refreshing live showtimes...', false, false);
    this.admin.run().subscribe({
      next: (response) => {
        this.scraping = false;
        if (response.alreadyRunning) {
          this.setScrapeMessage('A scrape is already running. Open Admin to watch progress.', false);
          return;
        }
        this.setScrapeMessage('Scrape started. Open Admin to watch progress.', false);
      },
      error: () => {
        this.scraping = false;
        this.setScrapeMessage('Backend is offline on localhost:3000. Start the backend, then refresh again.', true);
      },
    });
  }

  private setScrapeMessage(message: string, isError: boolean, autoHide = true) {
    if (this.messageTimer) clearTimeout(this.messageTimer);
    this.scrapeMessage = message;
    this.scrapeError = isError;
    if (autoHide) {
      this.messageTimer = setTimeout(() => {
        this.scrapeMessage = '';
        this.scrapeError = false;
      }, 5000);
    }
  }
}
